import { Labels } from '../../global/labels';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { makeDateFromString } from '../../global/helpers';
import {
    setInnerText,
    setUnsafeInnerHTML,
    TimeInSeconds,
} from '../../global/ui-helpers';
import { browser } from '../../global/browser';
import type {
    CountdownDateParts,
    CountdownMapFormat,
} from './ui-countdown.type';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr, Timeout } from '../../global/utils.type';
import styles from './ui-countdown.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICountdown
 * @element ui-countdown
 * @classdesc Represents a class for <code>ui-countdown</code> element.
 * Makes a counter with counts down every second until given date.
 * @property {string} expired {@attr expired} When deadline will happen.
 * Datetime in format 'dd.mm.yyyy hh:mm:ss' or 'yyyy-mm-dd hh:mm:ss' for
 * Lithuania.
 * @property {string} [format="dd:hh:mm:ss"] {@attr format} Countdown format.
 * @example
 * <ui-countdown expired="02.01.2020 00:00:00"></ui-countdown>
 */
@Component({ tag: 'ui-countdown', styles })
export class UICountdown extends UIElement {
    static readonly SECOND = 1000;

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-countdown', {
            days: 'Days',
            hours: 'Hours',
            minutes: 'Minutes',
            seconds: 'Seconds',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['expired'];
    }

    @Attribute(String) expired?: string;
    @Attribute(String) format: string = 'dd:hh:mm:ss';

    private remainingSecs: number = 0;
    private clockId: number | Timeout = -1;

    /**
     * Converts seconds to days, hours, minutes, seconds with leading zeros.
     * @returns {CountdownDateParts}
     * @private
     */
    private timeAsStr(): CountdownDateParts {
        // If more than 100 days then return 99 for everything to avoid grid overflow.
        if (this.remainingSecs > TimeInSeconds.Day * 100) {
            return {
                days: '99',
                hours: '23',
                minutes: '59',
                seconds: '59',
            };
        }

        if (this.remainingSecs <= 0) {
            return {
                days: '00',
                hours: '00',
                minutes: '00',
                seconds: '00',
            };
        }

        const format = (f: number) => String(f).padStart(2, '0');
        const parts: CountdownDateParts = {};
        let delta = this.remainingSecs;
        parts.days = format(Math.floor(delta / TimeInSeconds.Day));
        delta -= Number(parts.days) * TimeInSeconds.Day;
        parts.hours = format(Math.floor(delta / TimeInSeconds.Hour) % 24);
        delta -= Number(parts.hours) * TimeInSeconds.Hour;
        parts.minutes = format(
            Math.floor(delta / TimeInSeconds.Minute) % TimeInSeconds.Minute
        );
        delta -= Number(parts.minutes) * TimeInSeconds.Minute;
        parts.seconds = format(Math.floor(delta % TimeInSeconds.Minute));
        return parts;
    }

    /**
     * Ticks every second.
     * @private
     */
    private tick(): void {
        const deadline = makeDateFromString(this.expired!) || new Date();
        const expiredSecs = deadline.getTime();
        const nowSecs = new Date().getTime();
        this.remainingSecs =
            Math.floor(expiredSecs - nowSecs) / UICountdown.SECOND;
    }

    /**
     * Parses the format and returns its parts.
     * @returns {Array<string>}
     * @private
     */
    private parseFormat(): Array<string> {
        return this.format.split(':');
    }

    /**
     * Maps format to labels.
     * @type {{s: string, d: string, h: string, m: string}}
     * @private
     */
    get mapFormat(): CountdownMapFormat {
        return {
            d: 'days',
            h: 'hours',
            m: 'minutes',
            s: 'seconds',
        };
    }

    /**
     * Set the interval for countdown element.
     * @returns {number}
     * @private
     */
    private setClock(): number | Timeout {
        clearInterval(this.clockId);
        const ticker = () => {
            this.tick();
            const parts = this.timeAsStr();
            Object.keys(parts).forEach((key) => {
                this.querySelectorAll(`[data-${key}]`).forEach((el) => {
                    const span = el.querySelector('span');
                    const part = parts[key as keyof CountdownDateParts];
                    if (span && part !== undefined) {
                        setInnerText(span, part);
                    }
                });
            });
            if (this.remainingSecs <= 0) {
                clearInterval(this.clockId);
            }
        };
        ticker(); // call first time immediately.
        if (!browser.prefersReducedMotion()) {
            return setInterval(ticker, UICountdown.SECOND);
        } else {
            return -1;
        }
    }

    /**
     * Starts the clock.
     */
    startClock(): void {
        this.tick();
        this.clockId = this.setClock();
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'expired':
                this.startClock();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const mapFormat = this.parseFormat().map(
            (f) => this.mapFormat[f[0] as keyof CountdownMapFormat]
        );
        this.insertElements([
            {
                tagName: 'div',
                attributes: {
                    role: 'group',
                },
                classList: {
                    'ui-countdown__inner': true,
                },
                children: Object.keys(mapFormat).map((key, i) => {
                    const tag = this.createElement({
                        tagName: 'div',
                        classList: {
                            'ui-countdown__time': true,
                        },
                        attributes: {
                            'role': 'group',
                            'aria-label': UICountdown.labels[mapFormat[i]],
                            ['data-' + mapFormat[i]]: 'true',
                        },
                        children: [
                            {
                                tagName: 'span',
                            },
                            {
                                tagName: 'ins',
                                attributes: {
                                    'aria-hidden': true,
                                },
                                classList: {
                                    'ui-countdown__separator': true,
                                },
                                children: [':'],
                            },
                            {
                                tagName: 'ins',
                                classList: {
                                    'ui-countdown__label': true,
                                },
                                attributes: {
                                    'aria-hidden': 'true',
                                },
                                children: [UICountdown.labels[mapFormat[i]]],
                            },
                        ],
                    });
                    const span = tag.querySelector('span');
                    if (span) {
                        setUnsafeInnerHTML(span, '&nbsp;');
                    }
                    return tag;
                }),
            },
        ]);
        this.updateElement({
            attributes: {
                role: 'timer',
            },
        });
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.remainingSecs = 0;
        if (!this.expired) {
            console.warn(
                this.constructor.name +
                    ': used without attribute element. Zero value initialized.'
            );
            return;
        }
        this.startClock();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        clearInterval(this.clockId);
    }
}
